import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { string } from 'yup';
import { noop } from '@cbhq/cds-utils';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { TextInput } from '@cbhq/cds-web/controls';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalProps } from '@cbhq/cds-web/overlays';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { TextBody, TextHeadline } from '@cbhq/cds-web/typography';

import { useAppState } from ':cloud/contexts/AppStateContext';
import { useFindOrCreateToken } from ':cloud/hooks/useFindOrCreateToken';
import { useUpdateToken } from ':cloud/queries/ApiKeyQueries/useUpdateToken';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { generateStringValidationError } from ':cloud/utils/generateStringValidationError';

const domainSchema = string()
  .max(253, 'Must be less than 254 characters')
  .matches(/^(?!(http(s?)):\/\/).+/, {
    message: 'Must not include http:// or https://',
  })
  .matches(/^(?!:\/\/)(localhost|(?!(?:-))[a-zA-Z0-9]+([-][a-zA-Z0-9]+)*(\.[a-zA-Z]{2,})+)$/, {
    message: 'Must be a valid domain',
  });

type Domain = {
  domain: string;
  error: string | null;
  id: number;
};

type TokenConfigureModalBodyProps = {
  domainAllowlist: Domain[];
  setDomainAllowlist: Dispatch<SetStateAction<Domain[]>>;
};

function TokenConfigureModalBody({
  domainAllowlist,
  setDomainAllowlist,
}: TokenConfigureModalBodyProps) {
  const handleDomainWhitelistBlur = useCallback(
    (index: number) => {
      setDomainAllowlist(
        domainAllowlist.map((dw, idx) => {
          if (idx === index) {
            if (
              [...domainAllowlist.slice(0, idx), ...domainAllowlist.slice(idx + 1)]
                .map((dwl) => dwl.domain)
                .includes(dw.domain)
            ) {
              return { ...dw, error: 'Domain already exists' };
            }

            if (dw.domain !== '') {
              return {
                ...dw,
                error: generateStringValidationError(dw.domain, domainSchema) || null,
              };
            }

            return dw;
          }

          return dw;
        }),
      );
    },
    [domainAllowlist, setDomainAllowlist],
  );

  return (
    <VStack gap={4}>
      <TextHeadline as="p">Domain allowlist</TextHeadline>
      <TextBody as="p" color="foregroundMuted">
        Domains must include the full name, including any subdomains, and should omit the protocol
        (e.g., <code>https</code>). Wildcards are not currently supported.
      </TextBody>
      <VStack gap={2}>
        {domainAllowlist.map(({ id, domain, error }, index) => (
          <HStack gap={2} key={id}>
            <TextInput
              compact
              value={domain}
              onChange={(e) =>
                setDomainAllowlist(
                  domainAllowlist.map((dw, idx) =>
                    idx === index ? { ...dw, error: null, domain: e.target.value } : dw,
                  ),
                )
              }
              variant={error ? 'negative' : undefined}
              helperText={error || undefined}
              onBlur={() => handleDomainWhitelistBlur(index)}
              placeholder="example.com"
            />
            <Box>
              <IconButton
                name="minus"
                variant="secondary"
                onClick={() =>
                  setDomainAllowlist([
                    ...domainAllowlist.slice(0, index),
                    ...domainAllowlist.slice(index + 1),
                  ])
                }
              />
            </Box>
          </HStack>
        ))}
        {domainAllowlist.length < 100 && (
          <Box>
            <Button
              startIcon="add"
              variant="secondary"
              onPress={() =>
                setDomainAllowlist([
                  ...domainAllowlist,
                  { id: domainAllowlist.length, domain: '', error: null },
                ])
              }
            >
              Add domain
            </Button>
          </Box>
        )}
      </VStack>
    </VStack>
  );
}

type TokenRotateModalProps = Partial<ModalProps> & {
  onSuccess?: (clientId: string) => void;
  tokenId: string;
};

export function TokenConfigureModal(props: TokenRotateModalProps): JSX.Element {
  const { onRequestClose, onSuccess, tokenId } = props;
  const { activeOrg } = useGetUser();
  const { selectedProject } = useAppState();
  const toast = useToast();
  const [domainAllowlist, setDomainAllowlist] = useState<Domain[]>([
    { id: 0, domain: '', error: null },
  ]);

  const { token, isLoading: isTokensLoading } = useFindOrCreateToken(
    activeOrg?.organizationId,
    selectedProject?.id,
    selectedProject?.isTestnet,
  );

  useEffect(() => {
    if (!isTokensLoading && token.allowedDomains && token.allowedDomains.length > 0) {
      setDomainAllowlist(
        token.allowedDomains.map((domain, index) => ({
          id: index,
          domain,
          error: null,
        })),
      );
    }
  }, [isTokensLoading, token]);

  const { mutate: configureToken, isLoading: isConfigureLoading } = useUpdateToken(
    activeOrg?.organizationId,
    selectedProject?.id,
    (configuredTokenId) => {
      toast.show('Client API Key successfully configured.', {
        variant: 'positive',
      });
      onSuccess?.(configuredTokenId);
      onRequestClose?.();
    },
  );

  const handleSubmit = useCallback(() => {
    configureToken({
      tokenId,
      allowedDomains: domainAllowlist.filter((wl) => wl.domain !== '').map((wl) => wl.domain),
      updateMask: 'allowedDomains',
    });
  }, [configureToken, tokenId, domainAllowlist]);

  const isValidDomainAllowlist = useMemo(() => {
    const filteredDomainWhitelist = domainAllowlist.filter((dw) => dw.domain !== '');

    const isFilteredDomainWhitelistValid =
      filteredDomainWhitelist.length >= 1 &&
      filteredDomainWhitelist.every((dw) => dw.error === null);
    const isFilteredDomainWhitelistEmpty = filteredDomainWhitelist.length === 0;
    const hasFilteredDomainWhitelistChanged =
      filteredDomainWhitelist.map((dw) => dw.domain).join('') !== token.allowedDomains?.join('');

    return (
      (isFilteredDomainWhitelistValid || isFilteredDomainWhitelistEmpty) &&
      hasFilteredDomainWhitelistChanged
    );
  }, [domainAllowlist, token]);

  if (!tokenId) {
    throw new Error('Client API Key ID not available');
  }

  return (
    <Modal onRequestClose={onRequestClose || noop} visible {...props}>
      <ModalHeader title="Configure Client API Key" />
      <ModalBody>
        <TokenConfigureModalBody
          domainAllowlist={domainAllowlist}
          setDomainAllowlist={setDomainAllowlist}
        />
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button
            type="submit"
            disabled={!isValidDomainAllowlist || isTokensLoading}
            onPress={handleSubmit}
            loading={isConfigureLoading}
          >
            Confirm
          </Button>
        }
        secondaryAction={
          <Button variant="secondary" onPress={onRequestClose}>
            Cancel
          </Button>
        }
      />
    </Modal>
  );
}
